// Shipped as TEXT (`TPA_ESCAPE_INLINE_SOURCE`) into SFRA-proxied pages and into
// Worker pages, so this function must close over nothing at module scope.
// tpa-escape-runtime.test.ts evals the string to enforce that.
//
// Click-time capture, not `target=_blank` on the DOM: react-router's Link reads
// the `target` PROP, so a mutated attribute still SPA-navigates inside the
// frame. Capture on `document` runs before React's root listener, and RR skips
// its own navigation once `defaultPrevented`. Design: docs/tpa-link-escape.md.
export function tpaEscapeClickCapture(): void {
  // Partner iframe only. Reference compare never throws cross-origin.
  if (window.top === window.self) return;
  const bare = location.host.replace(/^tpa\./, "");

  document.addEventListener(
    "click",
    event => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!anchor) return;
      if (anchor.getAttribute("target") === "_blank") return;
      if (anchor.hasAttribute("download")) return;

      const raw = anchor.getAttribute("href") ?? "";
      if (raw.startsWith("#")) return;
      let target: URL;
      try {
        target = new URL(raw, location.href);
      } catch {
        return;
      }
      if (target.protocol !== "http:" && target.protocol !== "https:") return;

      if (target.host === location.host) {
        target.host = bare;
        // Partner attribution (AFID / GroupName / CID / utm_*) rides along, as
        // SFRA's tpa.js does. `hecFrameMode` is our simulation switch, not theirs.
        new URL(location.href).searchParams.forEach((value, key) => {
          if (key === "hecFrameMode" || target.searchParams.has(key)) return;
          target.searchParams.append(key, value);
        });
        target.searchParams.set("isTPA", "true");
      }

      event.preventDefault();
      // Returns null under "noopener" by spec, so a blocked popup (sandbox
      // without allow-popups) is undetectable here; SFRA's target=_blank is
      // equally blocked there.
      window.open(target.toString(), "_blank", "noopener");
    },
    true,
  );
}

export const TPA_ESCAPE_INLINE_SOURCE = `(${tpaEscapeClickCapture.toString()})();`;
